<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header Section -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1 scroll-reveal">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-indigo-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-indigo-600 tracking-widest">{{ datosEmpresa.nombreComercial }}</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Lista de Recepciones
          <span class="px-2 sm:px-3 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 text-xs sm:text-sm font-black rounded-lg border border-indigo-200 dark:border-indigo-800">
            {{ recepcionesFiltradas.length }}
          </span>
        </h1>
      </div>
      <!-- Botones de Excel -->
      <div class="flex items-center gap-2 w-full sm:w-auto">
        <button
          @click="exportarExcel"
          :disabled="recepciones.length === 0"
          class="px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Icon icon="mdi:file-excel" class="text-base sm:text-lg" />
          <span class="hidden sm:inline">Exportar Excel</span>
          <span class="sm:hidden">Excel</span>
        </button>
        <button
          @click="$refs.fileInput.click()"
          class="px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-md flex items-center gap-2"
        >
          <Icon icon="mdi:file-upload" class="text-base sm:text-lg" />
          <span class="hidden sm:inline">Importar Excel</span>
          <span class="sm:hidden">Importar</span>
        </button>
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,.xls"
          class="hidden"
          @change="importarExcel($event)"
        />
      </div>
    </div>

    <!-- Filters & Search -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5 animate-fadeInUp">
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-end">
        <div class="sm:col-span-7 lg:col-span-8">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Búsqueda Inteligente</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-2xl group-focus-within:text-indigo-500 transition-colors" />
            <input
              v-model="filtroBusqueda"
              type="text"
              placeholder="Buscar por orden, cliente, equipo o técnico..."
              class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm sm:text-base"
            />
          </div>
        </div>
        
        <div class="sm:col-span-3 lg:col-span-2">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Estado</label>
          <select
            v-model="filtroEstado"
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base"
          >
            <option value="TODOS">TODOS</option>
            <option value="EXPRESS">EXPRESS</option>
            <option v-for="est in estadosList" :key="est.idEstadoRecepcion" :value="est.nombre">{{ est.nombre?.toUpperCase() }}</option>
          </select>
        </div>

        <div class="sm:col-span-2 lg:col-span-2">
          <button 
            @click="cargarRecepciones"
            class="w-full py-2.5 sm:py-3.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs tracking-widest hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-600 shadow-sm"
          >
            <Icon icon="mdi:refresh" :class="{'animate-spin': loading}" class="text-lg sm:text-xl" />
            <span class="hidden sm:inline">Refrescar</span>
            <span class="sm:hidden">Refrescar</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden animate-fadeInUp delay-100">
      <!-- Versión Desktop: Tabla completa -->
      <div class="hidden lg:block overflow-x-auto custom-scrollbar">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Orden</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Equipo / Problema</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Acciones</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Cliente</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Fecha Ingreso</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Estado</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Total</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-left">Personal</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Diagnóstico</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Sede</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="loading" v-for="i in 5" :key="'load-'+i" class="animate-pulse">
              <td colspan="10" class="px-5 py-4"><div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full"></div></td>
            </tr>
            <tr v-if="!loading && recepcionesFiltradas.length === 0">
              <td colspan="10" class="px-5 py-16 text-center">
                <div class="flex flex-col items-center opacity-30 select-none">
                  <Icon icon="mdi:database-off-outline" class="text-7xl mb-3" />
                  <h4 class="text-xl font-black text-gray-400 tracking-widest">No hay registros</h4>
                  <p class="text-sm text-gray-400 mt-2" v-if="filtroEstado && filtroEstado !== 'TODOS'">
                    Filtro actual: <span class="font-bold">{{ filtroEstado }}</span>
                  </p>
                  <p class="text-sm text-gray-400 mt-1" v-if="filtroBusqueda">
                    Búsqueda: <span class="font-bold">{{ filtroBusqueda }}</span>
                  </p>
                </div>
              </td>
            </tr>
            <tr v-for="item in paginatedRecepciones" :key="item.idRecepcionEquipo" class="hover:bg-indigo-50/30 dark:hover:bg-gray-700/30 transition-all group">
              <td class="px-5 py-4">
                <span class="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg font-black text-xs border border-indigo-100 dark:border-indigo-800 transition-transform inline-block">
                  #{{ item.numeroOrden }}
                </span>
              </td>
              <td class="px-5 py-4">
                <div class="text-left">
                  <p class="font-black text-gray-900 dark:text-white text-xs mb-1 truncate max-w-[150px]">{{ item.descripcion }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 italic max-w-[150px] truncate" :title="item.problema">
                    {{ item.problema }}
                  </p>
                </div>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center justify-center gap-0.5 flex-nowrap whitespace-nowrap">
                  <button
                    @click="abrirWhatsappCliente(item)"
                    title="Enviar mensaje por WhatsApp"
                    class="p-1.5 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                  >
                    <Icon icon="mdi:whatsapp" class="text-base" />
                  </button>

                  <button
                    @click="generarPDFRecepcion(item)"
                    title="Generar PDF Formato Carta"
                    class="p-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                  >
                    <Icon icon="mdi:file-pdf-box" class="text-base" />
                  </button>

                  <button
                    v-if="item.nombreEstado?.toLowerCase() !== 'entregado'"
                    @click="abrirEditarModal(item)"
                    title="Editar Accesorios y Sede"
                    class="p-1.5 bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 rounded-lg hover:bg-violet-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                  >
                    <Icon icon="mdi:pencil" class="text-base" />
                  </button>

                  <button 
                    @click="$router.push(`/seguimiento/${item.numeroOrden}?tab=1`)"
                    title="Ver Detalle"
                    class="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                  >
                    <Icon icon="mdi:eye" class="text-base" />
                  </button>
                  
                  <button 
                    @click="imprimirTicketLocal(item)"
                    title="Imprimir Ticket"
                    class="p-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                  >
                    <Icon icon="mdi:printer" class="text-base" />
                  </button>
                  
                  <template v-if="item.nombreEstado?.toUpperCase() !== 'ENTREGADO'">
                    <button 
                      @click="$router.push(`/seguimiento/${item.numeroOrden}?tab=2`)"
                      title="Reparar / Editar Reparación"
                      class="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    >
                      <Icon icon="mdi:tools" class="text-base" />
                    </button>

                    <button 
                      v-if="item.nombreEstado?.toLowerCase().includes('imposible')"
                      @click="entregarSinPago(item)"
                      title="Entregar sin Cobro"
                      class="p-1.5 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-lg hover:bg-orange-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    >
                      <Icon icon="mdi:truck-delivery" class="text-base" />
                    </button>

                    <button 
                      v-if="item.nombreEstado?.toLowerCase().includes('garant')"
                      @click="finalizarGarantia(item)"
                      title="Finalizar y Entregar Garantía"
                      class="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    >
                      <Icon icon="mdi:check-circle-outline" class="text-base" />
                    </button>

                    <button 
                      v-if="item.nombreEstado?.toLowerCase() === 'reparado'"
                      @click="$router.push(`/seguimiento/${item.numeroOrden}?tab=3`)"
                      title="Pagar"
                      class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    >
                      <Icon icon="mdi:currency-usd" class="text-base" />
                    </button>

                    <button
                      v-if="esPorDiagnosticar(item)"
                      @click="abrirPagoExpress(item)"
                      title="Pasar a Diagnóstico Express ($10)"
                      class="p-1.5 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-300 rounded-lg hover:bg-yellow-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    >
                      <Icon icon="mdi:flash" class="text-base" />
                    </button>
                  </template>
                  
                  <div v-else class="flex items-center gap-0.5 flex-shrink-0">
                    <div class="px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg border border-green-200 dark:border-green-800 text-[10px] font-black uppercase flex items-center gap-1 shadow-sm" title="Orden Completada">
                      <Icon icon="mdi:check-decagram" class="text-base" />
                    </div>

                    <button 
                      @click="ponerEnGarantia(item)"
                      title="Pasar a Garantía"
                      class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    >
                      <Icon icon="mdi:shield-alert" class="text-base" />
                    </button>
                  </div>

                  <button
                    v-if="esAdmin"
                    @click="eliminarRegistro(item)"
                    title="Eliminar Permanente (Administrador)"
                    class="p-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                  >
                    <Icon icon="mdi:trash-can-outline" class="text-base" />
                  </button>
                </div>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center gap-3 text-left">
                  <div class="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center font-black text-gray-500 text-xs">
                    {{ item.nombreCliente?.charAt(0) }}
                  </div>
                  <div>
                    <p class="font-black text-gray-900 dark:text-white text-xs leading-tight">{{ item.nombreCliente }}</p>
                    <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">ID: {{ item.documentoCliente || 'S/D' }}</p>
                  </div>
                </div>
              </td>
              <td class="px-5 py-4 whitespace-nowrap">
                <div class="text-left space-y-1">
                  <div>
                    <p class="font-black text-gray-900 dark:text-white text-[10px] leading-none mb-0.5">ING: {{ formatearFecha(item.fechaIngreso) }}</p>
                    <p class="text-[9px] text-gray-400 font-black uppercase">{{ formatearHora(item.fechaIngreso) }}</p>
                  </div>
                  <div v-if="item.fechaReparacion" class="pt-1 border-t border-gray-100 dark:border-gray-700 animate-fadeIn">
                    <p class="font-black text-indigo-600 dark:text-indigo-400 text-[10px] leading-none mb-0.5">REP: {{ formatearFecha(item.fechaReparacion) }}</p>
                    <p class="text-[9px] text-indigo-400 font-black uppercase">{{ formatearHora(item.fechaReparacion) }}</p>
                  </div>
                </div>
              </td>
              <td class="px-5 py-4">
                <div :class="getEstadoStyle(item.nombreEstado)" class="px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest text-center border shadow-sm transition-all inline-block">
                  {{ item.nombreEstado }}
                </div>
              </td>
              <td class="px-5 py-4">
                <div v-if="debeMostrarTotal(item)" class="flex flex-col">
                  <span :class="{'line-through text-gray-400 text-[10px]': item.descuento > 0}" class="font-black text-gray-900 dark:text-white text-xs">
                    ${{ (item.precioTotalUsd || 0).toFixed(2) }}
                  </span>
                  <div v-if="item.descuento > 0" class="flex flex-col animate-fadeIn mt-1">
                    <span class="text-[9px] font-black text-rose-500 uppercase tracking-tighter">
                      DESC: -${{ item.descuento.toFixed(2) }}
                    </span>
                  </div>
                  <div v-if="item.montoPagadoDiagnosticoUsd > 0" class="flex flex-col mt-1">
                    <span class="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest leading-none">
                      EXPRESS:
                    </span>
                    <span class="text-xs font-black text-emerald-700 dark:text-emerald-300">
                      ${{ (item.montoPagadoDiagnosticoUsd || 0).toFixed(2) }}
                    </span>
                  </div>
                  <div v-if="item.montoPagadoReparacionUsd > 0" class="flex flex-col mt-1">
                    <span class="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest leading-none">
                      {{ (item.metodoPago?.toUpperCase().includes('CASHEA') || item.montoPagadoReparacionUsd < (item.precioTotalUsd - item.descuento)) ? 'Inicial:' : 'Pagado:' }}
                    </span>
                    <span class="text-xs font-black text-blue-700 dark:text-blue-300">
                      ${{ (item.montoPagadoReparacionUsd || 0).toFixed(2) }}
                    </span>
                  </div>
                </div>
                <div v-else class="text-gray-400 dark:text-gray-600 font-bold text-xs select-none">—</div>
              </td>
              <td class="px-5 py-4">
                <div class="flex flex-col gap-1.5 min-w-[120px]">
                  <div class="flex items-center gap-1.5">
                    <Icon icon="mdi:account-arrow-right" class="text-xs text-blue-500" />
                    <span class="text-[9px] font-bold text-gray-600 dark:text-gray-300 truncate">{{ item.nombreTecnicoRecepcion || item.NombreTecnicoRecepcion || item.nombreUsuario || item.NombreUsuario || 'Recepción' }}</span>
                  </div>
                  <div v-if="item.nombreTecnicoReparador || item.NombreTecnicoReparador" class="flex items-center gap-1.5">
                    <Icon icon="mdi:hammer-wrench" class="text-xs text-yellow-500" />
                    <span class="text-[9px] font-bold text-gray-600 dark:text-gray-300 truncate">{{ item.nombreTecnicoReparador || item.NombreTecnicoReparador }}</span>
                  </div>
                  <div v-if="item.nombreTecnicoEntrega || item.NombreTecnicoEntrega" class="flex items-center gap-1.5">
                    <Icon icon="mdi:truck-delivery" class="text-xs text-emerald-500" />
                    <span class="text-[9px] font-bold text-gray-600 dark:text-gray-300 truncate">{{ item.nombreTecnicoEntrega || item.NombreTecnicoEntrega }}</span>
                  </div>
                </div>
              </td>
              <td class="px-5 py-4">
                <div :class="[
                  'px-3 py-1 rounded-full text-[10px] font-black text-center border inline-flex items-center gap-1.5 uppercase tracking-widest',
                  ((item.tipoDiagnostico || item.TipoDiagnostico || '').toUpperCase().includes('EXPRE')) 
                    ? 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/30 dark:border-rose-800'
                    : 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/30 dark:border-emerald-800'
                ]">
                  <Icon :icon="((item.tipoDiagnostico || item.TipoDiagnostico || '').toUpperCase().includes('EXPRE')) ? 'mdi:flash' : 'mdi:heart-pulse'" class="text-xs" />
                  {{ (item.tipoDiagnostico || item.TipoDiagnostico || 'GRATIS') }}
                </div>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center gap-3">
                  <Icon icon="mdi:store-marker" class="text-amber-500 text-xl" />
                  <div>
                    <p class="font-black text-gray-900 dark:text-white text-xs leading-tight">{{ item.nombreSede || item.taller || 'Sede Central' }}</p>
                    <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest">UBICACIÓN</p>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Versión Mobile: Tarjetas -->
      <div class="lg:hidden space-y-3 p-2">
        <div v-if="loading" v-for="i in 3" :key="'load-mobile-'+i" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
          <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full mb-2"></div>
          <div class="h-3 bg-gray-100 dark:bg-gray-700 rounded w-3/4"></div>
        </div>
        
        <div v-if="!loading && recepcionesFiltradas.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <div class="flex flex-col items-center opacity-30 select-none">
            <Icon icon="mdi:database-off-outline" class="text-6xl mb-3" />
            <h4 class="text-lg font-black text-gray-400 tracking-widest">No hay registros</h4>
            <p class="text-sm text-gray-400 mt-2" v-if="filtroEstado && filtroEstado !== 'TODOS'">
              Filtro actual: <span class="font-bold">{{ filtroEstado }}</span>
            </p>
            <p class="text-sm text-gray-400 mt-1" v-if="filtroBusqueda">
              Búsqueda: <span class="font-bold">{{ filtroBusqueda }}</span>
            </p>
          </div>
        </div>
        
        <div v-for="item in paginatedRecepciones" :key="item.idRecepcionEquipo" 
             class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-all duration-200">
          
          <div class="flex flex-wrap items-center justify-between gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center gap-2">
              <span class="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg font-black text-xs border border-indigo-100 dark:border-indigo-800">
                #{{ item.numeroOrden }}
              </span>
              <div :class="getEstadoStyle(item.nombreEstado)" class="px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest border shadow-sm">
                {{ item.nombreEstado }}
              </div>
            </div>
            <div class="text-[9px] text-gray-400 font-black">
              {{ formatearFecha(item.fechaIngreso) }}
            </div>
          </div>

          <div class="flex items-center gap-2 mb-2">
            <div class="w-7 h-7 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center font-black text-gray-500 text-xs flex-shrink-0">
              {{ item.nombreCliente?.charAt(0) }}
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-black text-gray-900 dark:text-white text-xs leading-tight truncate">{{ item.nombreCliente }}</p>
              <p class="text-[8px] font-black text-gray-400 uppercase tracking-widest">ID: {{ item.documentoCliente || 'S/D' }}</p>
            </div>
          </div>

          <div class="mb-2 bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2">
            <p class="font-black text-gray-900 dark:text-white text-xs truncate">{{ item.descripcion }}</p>
            <p class="text-[10px] text-gray-500 dark:text-gray-400 italic truncate">{{ item.problema }}</p>
          </div>

          <div class="grid grid-cols-2 gap-1 mb-2 text-[10px]">
            <div class="flex items-center gap-1">
              <Icon icon="mdi:store-marker" class="text-amber-500 text-sm" />
              <span class="font-bold text-gray-600 dark:text-gray-400 truncate">{{ item.nombreSede || item.taller || 'Sede Central' }}</span>
            </div>
            <div class="flex items-center gap-1 justify-end">
              <div :class="[
                'px-2 py-0.5 rounded-full text-[8px] font-black text-center border inline-flex items-center gap-1 uppercase tracking-widest',
                ((item.tipoDiagnostico || item.TipoDiagnostico || '').toUpperCase().includes('EXPRE')) 
                  ? 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/30 dark:border-rose-800'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/30 dark:border-emerald-800'
              ]">
                <Icon :icon="((item.tipoDiagnostico || item.TipoDiagnostico || '').toUpperCase().includes('EXPRE')) ? 'mdi:flash' : 'mdi:heart-pulse'" class="text-[10px]" />
                {{ (item.tipoDiagnostico || item.TipoDiagnostico || 'GRATIS') }}
              </div>
            </div>
          </div>

          <div v-if="debeMostrarTotal(item)" class="flex flex-wrap items-center gap-2 mb-2 text-xs bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2">
            <span class="font-black text-gray-900 dark:text-white">${{ (item.precioTotalUsd || 0).toFixed(2) }}</span>
            <span v-if="item.descuento > 0" class="text-[9px] font-black text-rose-500 line-through">-${{ item.descuento.toFixed(2) }}</span>
            <span v-if="item.montoPagadoDiagnosticoUsd > 0" class="text-[9px] font-black text-emerald-600">EXPRESS: ${{ (item.montoPagadoDiagnosticoUsd || 0).toFixed(2) }}</span>
            <span v-if="item.montoPagadoReparacionUsd > 0" class="text-[9px] font-black text-blue-600">PAGO: ${{ (item.montoPagadoReparacionUsd || 0).toFixed(2) }}</span>
          </div>

          <div class="flex flex-wrap items-center gap-2 mb-3 text-[9px] bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2">
            <div class="flex items-center gap-1">
              <Icon icon="mdi:account-arrow-right" class="text-blue-500 text-sm" />
              <span class="font-bold text-gray-600 dark:text-gray-400 truncate">{{ item.nombreTecnicoRecepcion || item.NombreTecnicoRecepcion || 'Recepción' }}</span>
            </div>
            <div v-if="item.nombreTecnicoReparador || item.NombreTecnicoReparador" class="flex items-center gap-1">
              <Icon icon="mdi:hammer-wrench" class="text-yellow-500 text-sm" />
              <span class="font-bold text-gray-600 dark:text-gray-400 truncate">{{ item.nombreTecnicoReparador || item.NombreTecnicoReparador }}</span>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div class="flex items-center gap-1 mr-1">
              <button
                @click="abrirWhatsappCliente(item)"
                title="WhatsApp"
                class="p-1.5 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-600 hover:text-white transition-all shadow-sm"
              >
                <Icon icon="mdi:whatsapp" class="text-sm" />
              </button>

              <button
                @click="generarPDFRecepcion(item)"
                title="PDF Formato Carta"
                class="p-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-600 hover:text-white transition-all shadow-sm"
              >
                <Icon icon="mdi:file-pdf-box" class="text-sm" />
              </button>

              <button
                v-if="item.nombreEstado?.toLowerCase() !== 'entregado'"
                @click="abrirEditarModal(item)"
                title="Editar"
                class="p-1.5 bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 rounded-lg hover:bg-violet-600 hover:text-white transition-all shadow-sm"
              >
                <Icon icon="mdi:pencil" class="text-sm" />
              </button>

              <button 
                @click="$router.push(`/seguimiento/${item.numeroOrden}?tab=1`)"
                title="Ver Detalle"
                class="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm"
              >
                <Icon icon="mdi:eye" class="text-sm" />
              </button>
              
              <button 
                @click="imprimirTicketLocal(item)"
                title="Imprimir"
                class="p-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-600 hover:text-white transition-all shadow-sm"
              >
                <Icon icon="mdi:printer" class="text-sm" />
              </button>
            </div>
            
            <div class="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-0.5"></div>
            
            <div class="flex items-center gap-1 flex-wrap">
              <template v-if="item.nombreEstado?.toUpperCase() !== 'ENTREGADO'">
                <button 
                  @click="$router.push(`/seguimiento/${item.numeroOrden}?tab=2`)"
                  title="Reparar"
                  class="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                >
                  <Icon icon="mdi:tools" class="text-sm" />
                </button>

                <button 
                  v-if="item.nombreEstado?.toLowerCase().includes('imposible')"
                  @click="entregarSinPago(item)"
                  title="Entregar sin Cobro"
                  class="p-1.5 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-lg hover:bg-orange-600 hover:text-white transition-all shadow-sm"
                >
                  <Icon icon="mdi:truck-delivery" class="text-sm" />
                </button>

                <button 
                  v-if="item.nombreEstado?.toLowerCase().includes('garant')"
                  @click="finalizarGarantia(item)"
                  title="Finalizar Garantía"
                  class="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                >
                  <Icon icon="mdi:check-circle-outline" class="text-sm" />
                </button>

                <button 
                  v-if="item.nombreEstado?.toLowerCase() === 'reparado'"
                  @click="$router.push(`/seguimiento/${item.numeroOrden}?tab=3`)"
                  title="Pagar"
                  class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm"
                >
                  <Icon icon="mdi:currency-usd" class="text-sm" />
                </button>

                <button
                  v-if="esPorDiagnosticar(item)"
                  @click="abrirPagoExpress(item)"
                  title="Diagnóstico Express"
                  class="p-1.5 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-300 rounded-lg hover:bg-yellow-600 hover:text-white transition-all shadow-sm"
                >
                  <Icon icon="mdi:flash" class="text-sm" />
                </button>
              </template>
              
              <div v-else class="flex items-center gap-1">
                <div class="px-2 py-0.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg border border-green-200 dark:border-green-800 text-[8px] font-black uppercase flex items-center gap-1">
                  <Icon icon="mdi:check-decagram" class="text-sm" />
                </div>
                <button 
                  @click="ponerEnGarantia(item)"
                  title="Pasar a Garantía"
                  class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm"
                >
                  <Icon icon="mdi:shield-alert" class="text-sm" />
                </button>
              </div>
            </div>
            
            <div v-if="esAdmin" class="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-0.5"></div>
            
            <div v-if="esAdmin" class="flex items-center gap-1">
              <button
                @click="eliminarRegistro(item)"
                title="Eliminar"
                class="p-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-600 hover:text-white transition-all shadow-sm"
              >
                <Icon icon="mdi:trash-can-outline" class="text-sm" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="recepcionesFiltradas.length > 0" class="bg-gray-50/50 dark:bg-gray-800/50 px-3 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div class="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest text-center sm:text-left">
          MOSTRANDO {{ (currentPage - 1) * itemsPerPage + 1 }} - {{ Math.min(currentPage * itemsPerPage, recepcionesFiltradas.length) }} DE {{ recepcionesFiltradas.length }}
        </div>
        <div class="flex items-center gap-1 flex-wrap justify-center">
          <button @click="goToFirstPage" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-double-left" class="text-base sm:text-lg" />
          </button>
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-left" class="text-base sm:text-2xl" />
          </button>
          <div class="hidden sm:flex items-center gap-0.5 sm:gap-1 mx-1 sm:mx-2">
            <button v-for="page in displayedPages" :key="page" @click="currentPage = page" :class="['w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-[10px] sm:text-xs font-black transition-all flex items-center justify-center', currentPage === page ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600']">
              {{ page }}
            </button>
          </div>
          <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2">{{ currentPage }} / {{ totalPages }}</span>
          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-right" class="text-base sm:text-2xl" />
          </button>
          <button @click="goToLastPage" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-double-right" class="text-base sm:text-lg" />
          </button>
        </div>
      </div>
    </div>

    <!-- MODALES -->
    <Teleport to="body">
      <div v-if="showEditModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]" @click.self="showEditModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-violet-600 to-purple-600 px-4 sm:px-6 py-4 flex items-center gap-3">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:pencil-box" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-sm font-black text-white">Editar Recepción</h3>
              <p class="text-[10px] text-white/70 font-bold">#{{ editForm.numeroOrden }}</p>
            </div>
            <button @click="showEditModal = false" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 sm:p-6 space-y-5">
            <div>
              <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                <Icon icon="mdi:bag-personal" class="inline mr-1" /> Accesorios
              </label>
              <textarea
                v-model="editForm.accesorios"
                rows="3"
                placeholder="Ej: Cable, cargador, funda..."
                class="w-full px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-violet-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all resize-none"
                @blur="editForm.accesorios = toTitleCase(editForm.accesorios)"
              />
            </div>
            <div>
              <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                <Icon icon="mdi:store-marker" class="inline mr-1" /> Sede / Taller
              </label>
              <select
                v-model="editForm.idSede"
                class="w-full px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-violet-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer"
              >
                <option :value="null">— Sin sede asignada —</option>
                <option v-for="s in sedesList" :key="s.idSede" :value="s.idSede">{{ s.nombre }}</option>
              </select>
            </div>
          </div>
          <div class="px-4 sm:px-6 pb-6 flex flex-col sm:flex-row gap-3">
            <button @click="showEditModal = false" class="w-full sm:flex-1 py-3 border-2 border-gray-100 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
            <button @click="guardarEdicion" :disabled="savingEdit" class="w-full sm:flex-1 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-violet-500/30 disabled:opacity-50 flex items-center justify-center gap-2">
              <Icon :icon="savingEdit ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': savingEdit}" />
              {{ savingEdit ? 'Guardando...' : 'Guardar Cambios' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showEntregaRapidaModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
        <div class="bg-white dark:bg-gray-800 rounded-[1.5rem] shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-orange-500 to-amber-600 px-4 sm:px-6 py-5 flex items-center gap-3">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:truck-delivery" class="text-white text-2xl" />
            </div>
            <div>
              <h3 class="text-lg font-black text-white">Entrega sin Cobro</h3>
              <p class="text-[10px] text-white/70 font-bold uppercase tracking-widest">Orden #{{ entregaRapidaForm.item?.numeroOrden }}</p>
            </div>
            <button @click="showEntregaRapidaModal = false" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 sm:p-7 space-y-6">
            <div class="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-900/30 rounded-2xl">
              <p class="text-sm text-orange-800 dark:text-orange-300 font-medium leading-relaxed">
                Esta acción entregará el equipo <b>sin registrar ningún pago en caja</b>. Ideal para equipos imposibles de reparar o devoluciones.
              </p>
            </div>
            <div>
              <label class="block text-[11px] font-black text-gray-400 tracking-widest mb-3 uppercase">Técnico que entrega</label>
              <div class="flex items-center gap-3 p-3 sm:p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-2xl">
                <div class="p-2 bg-green-500 text-white rounded-xl">
                  <Icon icon="mdi:account-check" class="text-xl" />
                </div>
                <div>
                  <p class="font-black text-gray-800 dark:text-white text-sm">{{ obtenerNombreTecnicoLogueado() }}</p>
                  <p class="text-[9px] font-bold text-green-600 dark:text-green-400 uppercase tracking-widest">✓ Usuario logueado</p>
                </div>
              </div>
            </div>
            <div class="flex flex-col sm:flex-row gap-4">
              <button @click="showEntregaRapidaModal = false" class="w-full sm:flex-1 px-5 py-4 border-2 border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
              <button @click="confirmarEntregaSinPago" :disabled="loading" class="w-full sm:flex-1 px-5 py-4 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-orange-500/30 disabled:opacity-50 disabled:shadow-none hover:opacity-90 transition-all flex items-center justify-center gap-2">
                <Icon v-if="loading" icon="mdi:loading" class="animate-spin text-lg" />
                Confirmar Entrega
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showPagoExpressModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]">
        <div class="bg-white dark:bg-gray-900 rounded-[1.5rem] shadow-2xl w-full max-w-lg overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-yellow-500 to-amber-500 px-4 sm:px-6 py-5 flex items-center gap-3">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:flash-circle" class="text-white text-2xl" />
            </div>
            <div>
              <h3 class="text-lg font-black text-white">Confirmar Diagnóstico Express</h3>
              <p class="text-[10px] text-white/80 font-bold uppercase tracking-widest">Orden #{{ pagoExpressItem?.numeroOrden || pagoExpressItem?.NumeroOrden }}</p>
            </div>
            <button @click="cancelarPagoExpress" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 sm:p-7 space-y-6">
            <div class="rounded-3xl border border-yellow-100 dark:border-yellow-900/40 bg-yellow-50 dark:bg-yellow-900/20 p-5">
              <p class="text-sm font-black text-yellow-700 dark:text-yellow-200 leading-relaxed">
                Esta acción convertirá la recepción a <span class="uppercase">Diagnóstico Express</span> y registrará un pago de <b>${{ precioDiagnosticoExpress }} USD</b>.
              </p>
            </div>
            <div class="space-y-4">
              <div>
                <label class="block text-[11px] font-black text-gray-400 tracking-widest mb-3 uppercase">Método de Pago</label>
                <div class="grid grid-cols-2 gap-2">
                  <button v-for="metodo in metodosPago" :key="metodo.value" type="button" @click="pagoExpress.metodoPago = metodo.value" :class="['py-2.5 sm:py-3 px-3 sm:px-4 rounded-2xl border transition-all text-left text-[10px] sm:text-xs font-black', pagoExpress.metodoPago === metodo.value ? 'border-yellow-500 bg-yellow-100 text-yellow-900 shadow-sm' : 'border-gray-200 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200']">
                    <div class="flex items-center gap-2">
                      <Icon :icon="metodo.icon" class="text-sm sm:text-base" />
                      <span class="text-[10px] sm:text-xs">{{ metodo.label }}</span>
                    </div>
                  </button>
                </div>
              </div>
              <div v-if="pagoExpress.metodoPago && pagoExpress.metodoPago !== 'EFECTIVO_USD'">
                <label class="block text-[11px] font-black text-gray-400 tracking-widest mb-3 uppercase">Referencia de Pago</label>
                <input v-model="pagoExpress.referencia" type="text" placeholder="Ingresa referencia, número de transferencia, o datos de pago" class="w-full px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-yellow-500 rounded-2xl text-xs font-black text-gray-900 dark:text-white outline-none transition-all" />
              </div>
            </div>
            <div class="space-y-3 text-sm text-gray-600 dark:text-gray-300">
              <p><span class="font-black">Cliente:</span> {{ pagoExpressItem?.nombreCliente || pagoExpressItem?.NombreCliente || 'N/A' }}</p>
              <p><span class="font-black">Problema:</span> {{ pagoExpressItem?.problema || pagoExpressItem?.Problema || 'N/A' }}</p>
            </div>
            <div class="flex flex-col sm:flex-row gap-3">
              <button type="button" @click="cancelarPagoExpress" class="w-full sm:flex-1 px-5 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-2xl font-black text-xs uppercase tracking-widest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all">Cancelar</button>
              <button type="button" @click="confirmarPagoExpress" :disabled="procesandoPagoExpress || !pagoExpress.metodoPago" class="w-full sm:flex-1 px-5 py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                <Icon :icon="procesandoPagoExpress ? 'mdi:loading' : 'mdi:check-bold'" :class="{'animate-spin': procesandoPagoExpress}" class="text-base" />
                {{ procesandoPagoExpress ? 'Procesando...' : 'Confirmar Pago' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]">
        <div class="bg-white dark:bg-gray-800 rounded-[2rem] shadow-2xl w-full max-w-sm overflow-hidden animate-zoomIn border border-white/20">
          <div class="p-6 sm:p-8 text-center">
            <div :class="[confirmModalIconClass, 'w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-black/5']">
              <Icon :icon="confirmModalIcon" :class="[confirmModalIconColor, 'text-3xl sm:text-4xl']" />
            </div>
            <h3 class="text-lg sm:text-xl font-black text-gray-900 dark:text-white mb-2 leading-tight">{{ confirmModalTitle }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 font-medium leading-relaxed mb-8" v-html="confirmModalMessage"></p>
            <div class="flex flex-col gap-3">
              <button @click="aceptarConfirmacion" :class="[confirmModalConfirmClass, 'w-full py-4 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl transition-all active:scale-95']">{{ confirmModalConfirmText }}</button>
              <button @click="cancelarConfirmacion" class="w-full py-4 bg-gray-50 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:bg-gray-100 dark:hover:bg-gray-700 transition-all">{{ confirmModalCancelText }}</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import * as api from '@/api/recepcionEquipo.js';
import { obtenerServicios } from '@/api/servicios.js';
import { obtenerProductos } from '@/api/productos.js';
import { obtenerEstadosRecepcion } from '@/api/estadoRecepcion.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
import { obtenerTecnicos } from '@/api/tecnicos.js';
import { imprimirTicket } from '@/utils/ticketGenerator.js';
import { obtenerSedesActivas } from '@/api/sede.js';
import { obtenerClientePorId } from '@/api/clientes.js';
import { crearDetalleRecepcion } from '@/api/recepcionEquipo.js';
import { crearSalidaEquipo } from '@/api/salidaEquipoDetalle.js';
import { crearPagoSalidaEquipo } from '@/api/pagoSalidaEquipo.js';
import { toTitleCase } from '@/utils/stringUtils.js';
import { notificarRecepcion } from '@/utils/notificacionesHelper';
import { getCurrentUser } from "@/utils/permissionUtils";
import { obtenerDatosEmpresa } from '@/utils/tenantConfig.js';
import * as XLSX from 'xlsx';
import { descargarOrdenServicioPDF } from '@/utils/reporteOrdenServicioGenerator.js';

export default {
  name: 'RecepcionEquipoList',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      recepciones: [],
      loading: false,
      filtroBusqueda: '',
      filtroEstado: 'Por Diagnosticar', // Filtro por defecto
      estadosList: [],
      sedesList: [],
      catalogoServicios: [],
      catalogoProductos: [],
      tasaActiva: null,
      tecnicos: [],
      
      currentPage: 1,
      itemsPerPage: 5,
      maxVisiblePages: 10,

      showEditModal: false,
      savingEdit: false,
      editItemRef: null,
      editForm: {
        idRecepcionEquipo: null,
        numeroOrden: '',
        accesorios: '',
        idSede: null
      },
      
      showEntregaRapidaModal: false,
      entregaRapidaForm: {
        idTecnico: null,
        item: null,
        nombreTecnico: ''
      },
      
      showConfirmModal: false,
      confirmModalTitle: '',
      confirmModalMessage: '',
      confirmModalIcon: 'mdi:check-circle',
      confirmModalIconClass: 'bg-green-100 dark:bg-green-900/30',
      showPagoExpressModal: false,
      procesandoPagoExpress: false,
      pagoExpressItem: null,
      pagoExpress: {
        metodoPago: '',
        referencia: ''
      },
      metodosPago: [
        { value: 'EFECTIVO_USD', label: 'Efectivo USD', icon: 'mdi:cash-usd' },
        { value: 'TRANSFERENCIA', label: 'Transferencia', icon: 'mdi:bank-transfer' },
        { value: 'PAGO_MOVIL', label: 'Pago Móvil', icon: 'mdi:cellphone-nfc' },
        { value: 'ZELLE', label: 'Zelle', icon: 'mdi:send-circle' },
        { value: 'BINANCE', label: 'Binance/Cripto', icon: 'mdi:currency-btc' },
        { value: 'PUNTO_VENTA', label: 'Punto de Venta', icon: 'mdi:credit-card' }
      ],
      confirmModalIconColor: 'text-green-600 dark:text-green-400',
      confirmModalConfirmText: 'Aceptar',
      confirmModalCancelText: 'Cancelar',
      confirmModalType: 'success',
      confirmModalConfirmClass: 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700',
      confirmCallback: null,
      confirmCancelCallback: null,
      
      importando: false
    };
  },
  computed: {
    esAdmin() {
      return localStorage.getItem('rol') === 'Administrador';
    },
    datosEmpresa() {
      return obtenerDatosEmpresa();
    },
    precioDiagnosticoExpress() {
      // Precio configurable, por defecto $10
      return this.datosEmpresa?.precioDiagnosticoExpress || 10;
    },
    recepcionesFiltradas() {
      let result = this.recepciones;
      
      // Si hay búsqueda activa, buscamos en TODOS los registros (ignoramos filtros)
      if (this.filtroBusqueda && this.filtroBusqueda.trim() !== '') {
        const q = this.filtroBusqueda.toLowerCase().trim();
        result = result.filter(r => 
          r.numeroOrden?.toLowerCase().includes(q) ||
          r.nombreCliente?.toLowerCase().includes(q) ||
          r.documentoCliente?.toLowerCase().includes(q) ||
          r.descripcion?.toLowerCase().includes(q) ||
          r.taller?.toLowerCase().includes(q) ||
          r.nombreSede?.toLowerCase().includes(q) ||
          (r.tipoDiagnostico || r.TipoDiagnostico || '').toLowerCase().includes(q) ||
          r.nombreTecnicoRecepcion?.toLowerCase().includes(q) ||
          r.NombreTecnicoRecepcion?.toLowerCase().includes(q) ||
          r.nombreTecnicoReparador?.toLowerCase().includes(q) ||
          r.NombreTecnicoReparador?.toLowerCase().includes(q) ||
          r.nombreTecnicoEntrega?.toLowerCase().includes(q) ||
          r.NombreTecnicoEntrega?.toLowerCase().includes(q) ||
          r.nombreUsuario?.toLowerCase().includes(q) ||
          r.NombreUsuario?.toLowerCase().includes(q) ||
          r.problema?.toLowerCase().includes(q) ||
          r.Problema?.toLowerCase().includes(q) ||
          r.accesorios?.toLowerCase().includes(q) ||
          r.Accesorios?.toLowerCase().includes(q) ||
          (r.nombreEstado || r.NombreEstado || '').toLowerCase().includes(q)
        );
        return result;
      }
      
      // Si no hay búsqueda y el filtro es 'TODOS', mostramos todos
      if (this.filtroEstado === 'TODOS' || !this.filtroEstado) {
        return result;
      }
      
      // Si el filtro es EXPRESS
      if (this.filtroEstado === 'EXPRESS') {
        result = result.filter(r => {
          const diag = (r.tipoDiagnostico || r.TipoDiagnostico || '').toUpperCase();
          return diag.includes('EXPRE');
        });
        return result;
      }
      
      // Filtro por estado (case insensitive y flexible)
      const estadoFiltro = this.filtroEstado.toLowerCase();
      result = result.filter(r => {
        const estado = (r.nombreEstado || r.NombreEstado || '').toLowerCase();
        // Comparación exacta
        if (estado === estadoFiltro) return true;
        // Comparación sin guiones bajos ni espacios
        if (estado.replace(/[_\s]/g, '') === estadoFiltro.replace(/[_\s]/g, '')) return true;
        return false;
      });
      
      return result;
    },
    totalPages() {
      return Math.ceil(this.recepcionesFiltradas.length / this.itemsPerPage);
    },
    paginatedRecepciones() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.recepcionesFiltradas.slice(start, end);
    },
    displayedPages() {
      const total = this.totalPages;
      const max = this.maxVisiblePages;
      const current = this.currentPage;
      
      if (total <= max) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      
      let start = Math.max(1, current - Math.floor(max / 2));
      let end = start + max - 1;
      
      if (end > total) {
        end = total;
        start = Math.max(1, end - max + 1);
      }
      
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }
  },
  async mounted() {
    await this.cargarCatalogos();
    await this.cargarRecepciones();
    // Verificar si existe el estado "Por Diagnosticar" en la lista
    this.$nextTick(() => {
      const existeEstado = this.estadosList.some(e => {
        const nombre = (e.nombre || '').toLowerCase();
        return nombre === 'por diagnosticar' || 
               nombre.replace(/[_\s]/g, '') === 'pordiagnosticar';
      });
      if (!existeEstado && this.estadosList.length > 0) {
        // Si no existe, usar el primer estado de la lista o TODOS
        console.warn('El estado "Por Diagnosticar" no se encontró en la lista de estados');
        this.filtroEstado = 'TODOS';
      }
    });
  },
  methods: {
    toTitleCase,
    goToFirstPage() {
      this.currentPage = 1;
    },
    goToLastPage() {
      this.currentPage = this.totalPages;
    },
    
    // ============ GENERAR PDF ============
    async generarPDFRecepcion(item) {
      try {
        this.toast.info(`Generando PDF para orden #${item.numeroOrden}...`);
        
        let ordenCompleta = { ...item };
        try {
          const res = await api.obtenerRecepcionConRelaciones(item.idRecepcionEquipo);
          if (res.success && res.data) {
            ordenCompleta = res.data;
          }
        } catch (e) {
          console.warn('No se pudieron cargar relaciones', e);
        }

        if (item.nombreTecnicoRecepcion && !ordenCompleta.nombreTecnicoRecepcion) {
          ordenCompleta.nombreTecnicoRecepcion = item.nombreTecnicoRecepcion;
        }
        if (item.nombreTecnicoReparador && !ordenCompleta.nombreTecnicoReparador) {
          ordenCompleta.nombreTecnicoReparador = item.nombreTecnicoReparador;
        }
        if (item.nombreTecnicoEntrega && !ordenCompleta.nombreTecnicoEntrega) {
          ordenCompleta.nombreTecnicoEntrega = item.nombreTecnicoEntrega;
        }

        await descargarOrdenServicioPDF(ordenCompleta);
        this.toast.success(`PDF generado para orden #${item.numeroOrden}`);
        
      } catch (error) {
        console.error('Error generando PDF:', error);
        this.toast.error('Error al generar el PDF: ' + (error.message || 'Error desconocido'));
      }
    },

    // ============ WHATSAPP ============
    obtenerTelefonoCliente(item) {
      if (item.cliente && item.cliente.telefono) {
        return item.cliente.telefono;
      }
      if (item.telefonoCliente) return item.telefonoCliente;
      if (item.TelefonoCliente) return item.TelefonoCliente;
      if (item.telefono) return item.telefono;
      if (item.Telefono) return item.Telefono;
      return null;
    },

    abrirWhatsappCliente(item) {
      this.loading = true;
      
      let telefono = this.obtenerTelefonoCliente(item);
      
      if (!telefono && item.idCliente) {
        obtenerClientePorId(item.idCliente)
          .then(res => {
            if (res.success && res.data) {
              telefono = res.data.telefono || res.data.Telefono || '';
              this.enviarMensajeWhatsapp(item, telefono);
            } else {
              this.toast.error('No se pudo obtener el teléfono del cliente');
              this.loading = false;
            }
          })
          .catch(err => {
            console.error('Error obteniendo cliente:', err);
            this.toast.error('Error al obtener datos del cliente');
            this.loading = false;
          });
      } else {
        this.enviarMensajeWhatsapp(item, telefono);
      }
    },

    enviarMensajeWhatsapp(item, telefono) {
      if (!telefono) {
        this.toast.error(`El cliente ${item.nombreCliente || 'desconocido'} no tiene número de teléfono registrado.`);
        this.loading = false;
        return;
      }

      let cleanPhone = telefono.replace(/\D/g, '');
      
      if (cleanPhone.length === 10 && !cleanPhone.startsWith('58')) {
        cleanPhone = '58' + cleanPhone;
      } else if (cleanPhone.length === 11 && cleanPhone.startsWith('0')) {
        cleanPhone = '58' + cleanPhone.substring(1);
      } else if (cleanPhone.length === 10 && cleanPhone.startsWith('0')) {
        cleanPhone = '58' + cleanPhone.substring(1);
      }
      
      const empresa = this.datosEmpresa;
      const nombreCliente = item.nombreCliente || 'Cliente';
      const ordenNum = item.numeroOrden || '';
      const estado = item.nombreEstado || '';
      const equipo = item.descripcion || 'Equipo';
      const total = (item.precioTotalUsd || 0).toFixed(2);
      
      let mensaje = `Hola ${nombreCliente}, le escribimos de ${empresa.nombreComercial}\n\n`;
      mensaje += `Orden: #${ordenNum}\n`;
      mensaje += `Equipo: ${equipo}\n`;
      mensaje += `Estado actual: ${estado.toUpperCase()}\n`;
      mensaje += `Total: $${total}\n\n`;
      
      const estadoLower = (estado || '').toLowerCase();
      if (estadoLower.includes('reparado') || estadoLower.includes('listo')) {
        mensaje += `Su equipo ya esta reparado y listo para retirar.\n`;
        mensaje += `Puede pasar por nuestra sede en horario de atencion.\n\n`;
      } else if (estadoLower.includes('diagnosticar') || estadoLower.includes('diagnostico')) {
        mensaje += `Su equipo esta en proceso de diagnostico.\n`;
        mensaje += `En breve le estaremos informando los resultados.\n\n`;
      } else if (estadoLower.includes('imposible')) {
        mensaje += `Lamentamos informarle que su equipo no tiene reparacion factible.\n`;
        mensaje += `Puede pasar a retirarlo sin costo alguno.\n\n`;
      } else if (estadoLower.includes('espera') || estadoLower.includes('esperando')) {
        mensaje += `Su equipo esta en espera de repuestos o autorizacion.\n`;
        mensaje += `Nos comunicaremos con usted para coordinar.\n\n`;
      } else if (estadoLower.includes('garantia') || estadoLower.includes('garantía')) {
        mensaje += `Su equipo se encuentra en proceso de garantia.\n`;
        mensaje += `Le avisaremos cuando este listo para retirar.\n\n`;
      } else {
        mensaje += `Estamos trabajando en su equipo, le avisaremos cualquier novedad.\n\n`;
      }
      
      mensaje += `Sede: ${item.nombreSede || item.taller || 'Sede Central'}\n`;
      mensaje += `Contacto: ${empresa.telefono1 || empresa.whatsapp || ''}\n\n`;
      mensaje += `Este mensaje es automatico. Por favor, no responda a este mensaje.`;
      
      const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(mensaje)}`;
      window.open(url, '_blank');
      
      this.toast.success(`Abriendo conversación con ${nombreCliente}`);
      this.loading = false;
    },
    // ============ FIN WHATSAPP ============

    // ============ EXCEL - EXPORTAR ============
    exportarExcel() {
      try {
        if (this.recepciones.length === 0) {
          this.toast.warning('No hay datos para exportar');
          return;
        }

        const datos = this.recepciones.map(item => {
          let telefono = '';
          if (item.cliente && item.cliente.telefono) {
            telefono = item.cliente.telefono;
          } else if (item.telefonoCliente) {
            telefono = item.telefonoCliente;
          } else if (item.TelefonoCliente) {
            telefono = item.TelefonoCliente;
          } else if (item.telefono) {
            telefono = item.telefono;
          } else if (item.Telefono) {
            telefono = item.Telefono;
          }
          
          return {
            'N° Orden': item.numeroOrden || '',
            'Cliente': item.nombreCliente || '',
            'Documento': item.documentoCliente || '',
            'Teléfono': telefono || '',
            'Equipo': item.descripcion || '',
            'Problema': item.problema || '',
            'Estado': item.nombreEstado || '',
            'Fecha Ingreso': item.fechaIngreso ? new Date(item.fechaIngreso).toLocaleString('es-ES') : '',
            'Fecha Reparación': item.fechaReparacion ? new Date(item.fechaReparacion).toLocaleString('es-ES') : '',
            'Fecha Entrega': item.fechaEntrega ? new Date(item.fechaEntrega).toLocaleString('es-ES') : '',
            'Total USD': item.precioTotalUsd || 0,
            'Descuento USD': item.descuento || 0,
            'Diagnóstico': item.tipoDiagnostico || 'GRATIS',
            'Monto Diagnóstico': item.montoDiagnostico || 0,
            'Accesorios': item.accesorios || '',
            'Sede': item.nombreSede || item.taller || '',
            'Técnico Recepción': item.nombreTecnicoRecepcion || item.NombreTecnicoRecepcion || '',
            'Técnico Reparador': item.nombreTecnicoReparador || item.NombreTecnicoReparador || '',
            'Técnico Entrega': item.nombreTecnicoEntrega || item.NombreTecnicoEntrega || '',
            'Método Pago': item.metodoPago || '',
            'Referencia Pago': item.referenciaPago || '',
            'Monto Pagado Diagnóstico': item.montoPagadoDiagnosticoUsd || 0,
            'Monto Pagado Reparación': item.montoPagadoReparacionUsd || 0,
            'Actividades Realizadas': item.actividadesRealizadas || '',
            'Repuestos': item.repuestos || '',
            'Clave Patrón': item.clavePatron || ''
          };
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(datos);

        const colWidths = [
          { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 18 },
          { wch: 30 }, { wch: 30 }, { wch: 20 }, { wch: 20 },
          { wch: 20 }, { wch: 20 }, { wch: 12 }, { wch: 12 },
          { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 20 },
          { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 15 },
          { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 40 },
          { wch: 30 }, { wch: 15 }
        ];
        ws['!cols'] = colWidths;

        XLSX.utils.book_append_sheet(wb, ws, 'Recepciones');

        const fecha = new Date();
        const nombreArchivo = `Recepciones_${fecha.getFullYear()}-${String(fecha.getMonth()+1).padStart(2,'0')}-${String(fecha.getDate()).padStart(2,'0')}.xlsx`;

        XLSX.writeFile(wb, nombreArchivo);
        this.toast.success(`Exportados ${this.recepciones.length} registros a Excel`);
      } catch (error) {
        console.error('Error exportando Excel:', error);
        this.toast.error('Error al exportar: ' + error.message);
      }
    },
    // ============ EXCEL - IMPORTAR ============
    async importarExcel(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.importando = true;
      this.loading = true;

      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet);

            if (jsonData.length === 0) {
              this.toast.warning('El archivo está vacío');
              this.importando = false;
              this.loading = false;
              return;
            }

            this.mostrarConfirmacion({
              title: 'Importar Datos',
              message: `Se van a importar <b>${jsonData.length}</b> registros. ¿Deseas continuar?<br><br><span class="text-[10px] text-gray-400">Los registros se agregarán a la base de datos.</span>`,
              icon: 'mdi:file-upload',
              type: 'warning',
              confirmText: 'Sí, Importar',
              cancelText: 'Cancelar',
              onConfirm: async () => {
                await this.procesarImportacion(jsonData);
              },
              onCancel: () => {
                this.importando = false;
                this.loading = false;
                event.target.value = '';
              }
            });

          } catch (error) {
            console.error('Error procesando archivo:', error);
            this.toast.error('Error al procesar el archivo: ' + error.message);
            this.importando = false;
            this.loading = false;
            event.target.value = '';
          }
        };

        reader.readAsArrayBuffer(file);
      } catch (error) {
        console.error('Error importando Excel:', error);
        this.toast.error('Error al importar: ' + error.message);
        this.importando = false;
        this.loading = false;
        event.target.value = '';
      }
    },

    async procesarImportacion(datos) {
      let importados = 0;
      let errores = 0;

      try {
        const usuarioId = parseInt(localStorage.getItem('idUsuario') || 1);

        for (const row of datos) {
          try {
            const estadoNombre = (row['Estado'] || '').trim();
            let idEstado = 1;
            if (estadoNombre) {
              const estadoEncontrado = this.estadosList.find(e => 
                e.nombre?.toLowerCase() === estadoNombre.toLowerCase()
              );
              if (estadoEncontrado) {
                idEstado = estadoEncontrado.idEstadoRecepcion;
              }
            }

            const sedeNombre = (row['Sede'] || '').trim();
            let idSede = null;
            if (sedeNombre) {
              const sedeEncontrada = this.sedesList.find(s => 
                s.nombre?.toLowerCase() === sedeNombre.toLowerCase()
              );
              if (sedeEncontrada) {
                idSede = sedeEncontrada.idSede;
              }
            }

            const payload = {
              NumeroOrden: row['N° Orden'] || `IMP-${Date.now()}-${Math.floor(Math.random()*1000)}`,
              IdCliente: 1,
              IdUsuario: usuarioId,
              Problema: row['Problema'] || 'N/A',
              Descripcion: row['Equipo'] || 'N/A',
              Accesorios: row['Accesorios'] || 'Ninguno',
              IdSede: idSede,
              IdEstadoRecepcion: idEstado,
              IdTecnicoAsignado: null,
              IdTecnicoEntrega: null,
              IdTecnicoRecepcion: null,
              IdTecnicoReparador: null,
              ClavePatron: row['Clave Patrón'] || 'N/A',
              Taller: row['Sede'] || 'Sede Central',
              TipoDiagnostico: row['Diagnóstico'] || 'GRATIS',
              MontoDiagnostico: parseFloat(row['Monto Diagnóstico']) || 0,
              ActividadesRealizadas: row['Actividades Realizadas'] || '',
              Repuestos: row['Repuestos'] || '',
              FechaIngreso: row['Fecha Ingreso'] ? new Date(row['Fecha Ingreso']).toISOString() : new Date().toISOString(),
              PrecioTotalUsd: parseFloat(row['Total USD']) || 0,
              Descuento: parseFloat(row['Descuento USD']) || 0,
              metodoPago: row['Método Pago'] || '',
              referenciaPago: row['Referencia Pago'] || '',
              montoPagadoDiagnosticoUsd: parseFloat(row['Monto Pagado Diagnóstico']) || 0,
              montoPagadoReparacionUsd: parseFloat(row['Monto Pagado Reparación']) || 0
            };

            if (row['Fecha Reparación']) {
              payload.FechaReparacion = new Date(row['Fecha Reparación']).toISOString();
            }

            if (row['Fecha Entrega']) {
              payload.FechaEntrega = new Date(row['Fecha Entrega']).toISOString();
            }

            const res = await api.crearRecepcion(payload);
            if (res.success) {
              importados++;
            } else {
              errores++;
              console.warn('Error importando fila:', row, res.error);
            }

          } catch (error) {
            errores++;
            console.error('Error en fila:', row, error);
          }
        }

        await this.cargarRecepciones();

        let mensaje = `Importación completada. ${importados} registros importados correctamente.`;
        if (errores > 0) {
          mensaje += ` ${errores} registros con errores.`;
        }
        this.toast.success(mensaje);

      } catch (error) {
        console.error('Error en procesarImportacion:', error);
        this.toast.error('Error al importar: ' + error.message);
      } finally {
        this.importando = false;
        this.loading = false;
        this.$refs.fileInput.value = '';
      }
    },
    // ============ FIN EXCEL ============

    async cargarRecepciones() {
      this.loading = true;
      try {
        const res = await api.obtenerRecepciones();
        if (res.success) {
          this.recepciones = res.data;
        }
      } catch (error) {
        console.error("Error cargando recepciones", error);
        this.toast.error('Error al cargar los datos: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    async cargarCatalogos() {
      try {
        const [eRes, sRes, pRes, tRes, tecRes, sedRes] = await Promise.all([
          obtenerEstadosRecepcion(true),
          obtenerServicios('activos'),
          obtenerProductos('activos'),
          obtenerTasaVentaActual(),
          obtenerTecnicos(),
          obtenerSedesActivas()
        ]);
        
        if (eRes.success) this.estadosList = eRes.data;
        if (sRes.success) this.catalogoServicios = sRes.data;
        if (pRes.success) this.catalogoProductos = pRes.data;
        if (tRes.success) this.tasaActiva = tRes.data;
        if (tecRes.success) this.tecnicos = tecRes.data || [];
        if (sedRes.success) this.sedesList = sedRes.data || [];
      } catch (error) {
        console.error("Error catálogos", error);
      }
    },

    abrirEditarModal(item) {
      this.editItemRef = item;
      this.editForm = {
        idRecepcionEquipo: item.idRecepcionEquipo,
        numeroOrden: item.numeroOrden,
        accesorios: item.accesorios || '',
        idSede: item.idSede || null
      };
      this.showEditModal = true;
    },

    async guardarEdicion() {
      if (this.savingEdit) return;
      this.savingEdit = true;
      try {
        const item = this.editItemRef;
        const payload = {
          IdRecepcionEquipo: item.idRecepcionEquipo || item.IdRecepcionEquipo,
          NumeroOrden: item.numeroOrden || item.NumeroOrden,
          IdCliente: item.idCliente || item.IdCliente,
          IdUsuario: item.idUsuario || item.IdUsuario || parseInt(localStorage.getItem('idUsuario') || 1),
          Problema: item.problema || item.Problema || 'N/A',
          Descripcion: item.descripcion || item.Descripcion || 'N/A',
          Accesorios: this.editForm.accesorios || 'Ninguno',
          IdSede: this.editForm.idSede,
          IdEstadoRecepcion: item.idEstadoRecepcion || item.IdEstadoRecepcion,
          IdTecnicoAsignado: item.idTecnicoAsignado || item.IdTecnicoAsignado,
          IdTecnicoEntrega: item.idTecnicoEntrega || item.IdTecnicoEntrega,
          IdTecnicoRecepcion: item.idTecnicoRecepcion || item.IdTecnicoRecepcion,
          IdTecnicoReparador: item.idTecnicoReparador || item.IdTecnicoReparador,
          ClavePatron: item.clavePatron || item.ClavePatron || 'N/A',
          Taller: item.taller || item.Taller || 'Sede Central',
          TipoDiagnostico: item.tipoDiagnostico || item.TipoDiagnostico || 'Gratis (24-48h)',
          MontoDiagnostico: item.montoDiagnostico || item.MontoDiagnostico || 0,
          ActividadesRealizadas: item.actividadesRealizadas || item.ActividadesRealizadas || 'N/A',
          Repuestos: item.repuestos || item.Repuestos || '',
          FechaIngreso: item.fechaIngreso || item.FechaIngreso || new Date().toISOString(),
          IdProductoServicios: item.idProductoServicios || item.IdProductoServicios
        };

        const res = await api.actualizarRecepcion(item.idRecepcionEquipo, payload);
        if (res.success) {
          Object.assign(item, {
            accesorios: this.editForm.accesorios,
            idSede: this.editForm.idSede,
            nombreSede: this.sedesList.find(s => s.idSede === this.editForm.idSede)?.nombre || item.nombreSede
          });
          this.toast.success('Recepción actualizada correctamente');
          notificarRecepcion('Recepción actualizada', `Orden #${this.editForm.numeroOrden}`, `Accesorios: ${this.editForm.accesorios || 'N/A'}`);
          this.showEditModal = false;
        } else {
          this.toast.error(res.error || 'No se pudo actualizar');
        }
      } catch (e) {
        this.toast.error('Error: ' + e.message);
      } finally {
        this.savingEdit = false;
      }
    },
    
    obtenerNombreTecnicoLogueado() {
      if (this.entregaRapidaForm.nombreTecnico) {
        return this.entregaRapidaForm.nombreTecnico;
      }
      
      const user = getCurrentUser();
      if (user) {
        const tecEncontrado = this.tecnicos.find(t => 
          t.idUsuario === user.idUsuario || t.IdUsuario === user.idUsuario
        );
        if (tecEncontrado) {
          return `${tecEncontrado.nombre || tecEncontrado.Nombre} ${tecEncontrado.apellido || tecEncontrado.Apellido}`.trim() || 'Técnico';
        }
        return user.nombre || user.username || 'Usuario logueado';
      }
      return 'Usuario no identificado';
    },

    async entregarSinPago(item) {
      const user = getCurrentUser();
      let idTecnico = null;
      let nombreTecnico = '';
      
      if (user) {
        const tecEncontrado = this.tecnicos.find(t => 
          t.idUsuario === user.idUsuario || t.IdUsuario === user.idUsuario
        );
        if (tecEncontrado) {
          idTecnico = tecEncontrado.idTecnico || tecEncontrado.IdTecnico;
          nombreTecnico = `${tecEncontrado.nombre || tecEncontrado.Nombre} ${tecEncontrado.apellido || tecEncontrado.Apellido}`.trim();
        } else {
          idTecnico = user.idUsuario || user.id;
          nombreTecnico = user.nombre || user.username || 'Usuario logueado';
        }
      }
      
      if (!idTecnico) {
        idTecnico = parseInt(localStorage.getItem('idUsuario') || null);
        nombreTecnico = localStorage.getItem('nombre') || 'Usuario logueado';
      }
      
      this.entregaRapidaForm.item = item;
      this.entregaRapidaForm.idTecnico = idTecnico;
      this.entregaRapidaForm.nombreTecnico = nombreTecnico || 'Técnico logueado';
      this.showEntregaRapidaModal = true;
    },

    async confirmarEntregaSinPago() {
      if (!this.entregaRapidaForm.idTecnico) {
        this.toast.error('No se pudo identificar al técnico logueado.');
        return;
      }
      
      this.loading = true;
      try {
        const item = this.entregaRapidaForm.item;
        const estadoEntregado = this.estadosList.find(e => (e.nombre || '').toUpperCase() === 'ENTREGADO');
        if (!estadoEntregado) throw new Error('No se encontró el estado "ENTREGADO" en el catálogo.');

        const payload = {
          ...item,
          IdEstadoRecepcion: estadoEntregado.idEstadoRecepcion,
          IdTecnicoEntrega: this.entregaRapidaForm.idTecnico,
          FechaEntrega: new Date().toISOString()
        };

        const res = await api.actualizarRecepcion(item.idRecepcionEquipo, payload);
        if (res.success) {
          this.toast.success(`Orden #${item.numeroOrden} entregada correctamente.`);
          this.showEntregaRapidaModal = false;
          await this.cargarRecepciones();
        } else {
          this.toast.error(res.error || 'No se pudo actualizar el estado.');
        }
      } catch (error) {
        this.toast.error('Error: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    formatearFecha(fecha) {
      if (!fecha) return '---';
      return new Date(fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    formatearHora(fecha) {
      if (!fecha) return '';
      return new Date(fecha).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    },
    getEstadoStyle(estado) {
      if (!estado) return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/40 dark:text-gray-300 dark:border-gray-800';
      
      const e = estado.toLowerCase();
      const estadoConfig = this.estadosList?.find(est => est.nombre?.toLowerCase() === e);
      
      if (estadoConfig && estadoConfig.color) {
        const colorMap = {
          'success': 'green', 'warning': 'amber', 'danger': 'red', 'info': 'cyan',
          'primary': 'blue', 'purple': 'purple', 'indigo': 'indigo', 'pink': 'pink',
          'orange': 'orange', 'teal': 'teal', 'fuchsia': 'fuchsia', 'lime': 'lime',
          'emerald': 'emerald', 'rose': 'rose', 'cyan': 'cyan', 'sky': 'sky',
          'violet': 'violet'
        };
        const baseColor = colorMap[estadoConfig.color.toLowerCase()] || 'gray';
        return `bg-${baseColor}-500 text-white border-${baseColor}-600 shadow-sm font-black`;
      }

      if (e.includes('diagno')) return 'bg-amber-500 text-white border-amber-600 shadow-sm font-black';
      if (e.includes('reparado')) return 'bg-green-500 text-white border-green-600 shadow-sm font-black';
      if (e.includes('entrega')) return 'bg-blue-500 text-white border-blue-600 shadow-sm font-black';
      if (e.includes('espera')) return 'bg-purple-500 text-white border-purple-600 shadow-sm font-black';
      
      return 'bg-gray-500 text-white border-gray-600 shadow-sm font-black';
    },
    
    async eliminarRegistro(item) {
      this.mostrarConfirmacion({
        title: '¿Eliminar Recepción?',
        message: `Estás a punto de borrar la orden <b>${item.numeroOrden}</b>.`,
        icon: 'mdi:alert-octagon',
        type: 'danger',
        confirmText: 'Sí, eliminar',
        cancelText: 'Cancelar',
        onConfirm: async () => {
          try {
            const res = await api.eliminarRecepcion(item.idRecepcionEquipo);
            if (res.success) {
              this.mostrarToastExito('Registro eliminado');
              await this.cargarRecepciones();
            } else {
              this.mostrarToastError(res.error || 'No se pudo eliminar');
            }
          } catch (error) {
            this.mostrarToastError('Error: ' + error.message);
          }
        }
      });
    },
    
    mostrarToastExito(mensaje) { this.toast.success(mensaje, { timeout: 3000 }); },
    mostrarToastError(mensaje) { this.toast.error(mensaje, { timeout: 5000 }); },
    
    mostrarConfirmacion(options) {
      this.confirmModalTitle = options.title || 'Confirmar';
      this.confirmModalMessage = options.message || '¿Estás seguro?';
      this.confirmModalIcon = options.icon || 'mdi:help-circle';
      this.confirmModalIconClass = options.iconClass || 'bg-blue-100 dark:bg-blue-900/30';
      this.confirmModalIconColor = options.iconColor || 'text-blue-600 dark:text-blue-400';
      this.confirmModalConfirmText = options.confirmText || 'Aceptar';
      this.confirmModalCancelText = options.cancelText || 'Cancelar';
      this.confirmModalType = options.type || 'confirm';
      
      if (options.type === 'danger') this.confirmModalConfirmClass = 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700';
      else if (options.type === 'success') this.confirmModalConfirmClass = 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700';
      else if (options.type === 'warning') this.confirmModalConfirmClass = 'bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-700 hover:to-amber-700';
      else this.confirmModalConfirmClass = 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700';
      
      this.confirmCallback = options.onConfirm || null;
      this.confirmCancelCallback = options.onCancel || null;
      this.showConfirmModal = true;
    },
    
    cancelarConfirmacion() { this.showConfirmModal = false; if (this.confirmCancelCallback) this.confirmCancelCallback(); },
    aceptarConfirmacion() { this.showConfirmModal = false; if (this.confirmCallback) this.confirmCallback(); },
    
    esPorDiagnosticar(item) {
      const estado = ((item.nombreEstado || item.NombreEstado || '') + '').toLowerCase().replace(/_/g, ' ');
      const tipoDiag = ((item.tipoDiagnostico || item.TipoDiagnostico || '') + '').toLowerCase();
      return estado.includes('por diagnosticar') && !tipoDiag.includes('expre');
    },
    
    debeMostrarTotal(item) {
      const estado = ((item.nombreEstado || item.NombreEstado || '') + '').toLowerCase();
      const tipoDiag = ((item.tipoDiagnostico || item.TipoDiagnostico || '') + '').toLowerCase();
      const esExpress = tipoDiag.includes('expre') || (item.montoPagadoDiagnosticoUsd || 0) > 0;
      
      if (esExpress) return true;
      if (estado.includes('diagnost') || estado.includes('imposible')) {
        return false;
      }
      return true;
    },
    
    abrirPagoExpress(item) {
      this.pagoExpressItem = item;
      this.pagoExpress = { metodoPago: '', referencia: '' };
      this.showPagoExpressModal = true;
    },
    
    cancelarPagoExpress() {
      this.showPagoExpressModal = false;
      this.pagoExpressItem = null;
      this.pagoExpress = { metodoPago: '', referencia: '' };
    },
    
    async confirmarPagoExpress() {
      if (!this.pagoExpress.metodoPago) {
        this.toast.error('Selecciona un método de pago para continuar.');
        return;
      }

      const item = this.pagoExpressItem;
      if (!item) return;

      const precioExpress = this.precioDiagnosticoExpress;

      this.procesandoPagoExpress = true;
      try {
        const usuarioId = parseInt(localStorage.getItem('idUsuario') || '1');
        const idRecepcion = item.idRecepcionEquipo || item.IdRecepcionEquipo;
        const estadoRecepcionId = item.idEstadoRecepcion || item.IdEstadoRecepcion || 1;
        const tecnicoId = item.idTecnicoAsignado || item.IdTecnicoAsignado || item.idTecnicoRecepcion || item.IdTecnicoRecepcion || item.idUsuario || item.IdUsuario || usuarioId;

        const actualizarPayload = {
          IdRecepcionEquipo: idRecepcion,
          NumeroOrden: item.numeroOrden || item.NumeroOrden,
          IdProductoServicios: item.idProductoServicios || item.IdProductoServicios,
          FechaIngreso: item.fechaIngreso || item.FechaIngreso,
          FechaReparacion: item.fechaReparacion || item.FechaReparacion,
          FechaEntrega: item.fechaEntrega || item.FechaEntrega,
          FechaPago: item.fechaPago || item.FechaPago,
          Problema: item.problema || item.Problema || 'N/A',
          Descripcion: item.descripcion || item.Descripcion || 'N/A',
          Repuestos: item.repuestos || item.Repuestos || '',
          Accesorios: item.accesorios || item.Accesorios || 'Ninguno',
          IdCliente: item.idCliente || item.IdCliente,
          IdUsuario: item.idUsuario || item.IdUsuario || usuarioId,
          IdTecnicoAsignado: item.idTecnicoAsignado || item.IdTecnicoAsignado,
          IdTecnicoEntrega: item.idTecnicoEntrega || item.IdTecnicoEntrega,
          IdTecnicoRecepcion: item.idTecnicoRecepcion || item.IdTecnicoRecepcion,
          IdTecnicoReparador: item.idTecnicoReparador || item.IdTecnicoReparador,
          ClavePatron: item.clavePatron || item.ClavePatron || 'N/A',
          Taller: item.taller || item.Taller || 'Sede Central',
          TipoDiagnostico: 'EXPRESS',
          MontoDiagnostico: precioExpress,
          ActividadesRealizadas: item.actividadesRealizadas || item.ActividadesRealizadas || '',
          IdEstadoRecepcion: estadoRecepcionId,
          IdSede: item.idSede || item.IdSede
        };

        const resUpdate = await api.actualizarRecepcion(idRecepcion, actualizarPayload);
        if (!resUpdate.success) throw new Error(resUpdate.error || 'No se pudo actualizar la orden.');

        const detalleRes = await crearDetalleRecepcion(idRecepcion, {
          cantidad: 1,
          precioUnitarioUsd: 0,
          subtotalUsd: 0,
          precioUnitarioBs: 0,
          subtotalBs: 0,
          idTecnico: tecnicoId,
          idEstadoRecepcion: estadoRecepcionId,
          observaciones: 'Diagnóstico Express'
        });
        if (!detalleRes.success) throw new Error(detalleRes.error || 'No se pudo crear el detalle de diagnóstico express.');

        const idDetalle = detalleRes.data?.idRecepcionEquipoDetalle || detalleRes.data?.IdRecepcionEquipoDetalle;
        if (!idDetalle) throw new Error('No se encontró el detalle de recepción generado.');

        const salidaRes = await crearSalidaEquipo({
          precioUnitarioUsd: 0,
          precioUnitarioBs: 0,
          idRecepcionEquipoDetalle: idDetalle,
          idUsuario: usuarioId,
          idEstadoRecepcion: estadoRecepcionId
        });
        if (!salidaRes.success) throw new Error(salidaRes.error || 'No se pudo crear la salida para pago express.');

        const idSalida = salidaRes.data?.idSalidaEquipoDetalle || salidaRes.data?.IdSalidaEquipoDetalle;
        if (!idSalida) throw new Error('No se obtuvo la salida de equipo generada.');

        const tasaRes = await obtenerTasaVentaActual();
        const tasaData = tasaRes.success && tasaRes.data ? tasaRes.data : {};
        const tasaUsdBs = parseFloat(tasaData.PrecioUsdBs || tasaData.precioUsdBs || tasaData.precioUsdBs || 1) || 1;
        const idFuenteTasa = tasaData.IdFuenteTasa || tasaData.idFuenteTasa || 1;
        const idTasaVenta = tasaData.IdTasaVenta || tasaData.idTasaVenta || 1;

        const pagoRes = await crearPagoSalidaEquipo({
          idSalidaEquipoDetalle: idSalida,
          numeroRecibo: `DIAG-${item.numeroOrden || item.NumeroOrden || idRecepcion}`,
          fechaPago: new Date().toISOString(),
          montoUsd: precioExpress,
          montoBs: +(precioExpress * tasaUsdBs).toFixed(2),
          tasaBsUsd: tasaUsdBs,
          idFuenteTasa: parseInt(idFuenteTasa, 10),
          idTasaVenta: parseInt(idTasaVenta, 10),
          tipoPago: 'EXPRESS',
          metodoPago: this.pagoExpress.metodoPago,
          referencia: this.pagoExpress.referencia || null,
          idUsuario: usuarioId,
          estado: 'Completado',
          observaciones: 'Pago Diagnóstico Express'
        });
        if (!pagoRes.success) throw new Error(pagoRes.error || 'No se pudo registrar el pago express.');

        this.toast.success(`Orden #${item.numeroOrden || item.NumeroOrden} actualizada a Diagnóstico Express y se registró el pago.`);
        this.cancelarPagoExpress();
        await this.cargarRecepciones();
      } catch (error) {
        console.error('Error en confirmarPagoExpress:', error);
        this.toast.error('Error: ' + (error.message || error));
      } finally {
        this.procesandoPagoExpress = false;
      }
    },
    
    async ponerEnGarantia(item) {
      this.mostrarConfirmacion({
        title: '¿Pasar a Garantía?',
        message: `¿Estás seguro de que deseas poner la orden <b>${item.numeroOrden}</b> en Garantía?`,
        icon: 'mdi:shield-alert',
        type: 'warning',
        confirmText: 'Sí, pasar a Garantía',
        onConfirm: async () => {
          this.loading = true;
          try {
            const estadoGarantia = this.estadosList.find(e => 
              (e.nombre || '').toLowerCase().includes('garantia') || 
              (e.nombre || '').toLowerCase().includes('garantía')
            );
            
            if (!estadoGarantia) throw new Error('No se encontró el estado "GARANTÍA" en el catálogo.');

            const res = await api.cambiarEstadoSimple(item.idRecepcionEquipo, estadoGarantia.idEstadoRecepcion, 'Pasado a Garantía');
            
            if (res.success) {
              this.toast.success(`Orden #${item.numeroOrden} ahora está en GARANTÍA.`);
              await this.cargarRecepciones();
            } else {
              this.toast.error(res.error || 'No se pudo actualizar el estado.');
            }
          } catch (error) {
            console.error('❌ Error en ponerEnGarantia:', error);
            this.toast.error('Error: ' + error.message);
          } finally {
            this.loading = false;
          }
        }
      });
    },

    async finalizarGarantia(item) {
      this.mostrarConfirmacion({
        title: '¿Finalizar Garantía?',
        message: `¿Estás seguro de que deseas finalizar la garantía y entregar la orden <b>${item.numeroOrden}</b>?<br><br><span class="text-[10px] text-gray-400">El estado pasará a ENTREGADO sin requerir pagos adicionales.</span>`,
        icon: 'mdi:check-circle',
        type: 'success',
        confirmText: 'Sí, finalizar y entregar',
        onConfirm: async () => {
          this.loading = true;
          try {
            const estadoEntregado = this.estadosList.find(e => 
              (e.nombre || '').toLowerCase().includes('entregado')
            );
            
            if (!estadoEntregado) throw new Error('No se encontró el estado "ENTREGADO" en el catálogo.');

            const res = await api.cambiarEstadoSimple(item.idRecepcionEquipo, estadoEntregado.idEstadoRecepcion, 'Garantía Finalizada');
            
            if (res.success) {
              this.toast.success(`Garantía de la orden #${item.numeroOrden} finalizada con éxito.`);
              await this.cargarRecepciones();
            } else {
              this.toast.error(res.error || 'No se pudo finalizar la garantía.');
            }
          } catch (error) {
            console.error('❌ Error en finalizarGarantia:', error);
            this.toast.error('Error: ' + error.message);
          } finally {
            this.loading = false;
          }
        }
      });
    },

    async imprimirTicketLocal(item) {
      this.toast.info(`Generando ticket para orden ${item.numeroOrden}...`);
      
      let ordenCompleta = { ...item };
      let pagoInfo = null;
      
      try {
        const res = await api.obtenerRecepcionConRelaciones(item.idRecepcionEquipo);
        if (res.success && res.data) {
          ordenCompleta = res.data;
        }
        
        try {
          const { obtenerPagosPorSalidaDetalle } = await import('@/api/pagoSalidaEquipo.js');
          
          if (ordenCompleta.detalles && ordenCompleta.detalles.length > 0) {
            for (const detalle of ordenCompleta.detalles) {
              if (detalle.salidasEquipoDetalle && detalle.salidasEquipoDetalle.length > 0) {
                for (const salida of detalle.salidasEquipoDetalle) {
                  if (salida.idSalidaEquipoDetalle) {
                    const pagosRes = await obtenerPagosPorSalidaDetalle(salida.idSalidaEquipoDetalle);
                    if (pagosRes.success && pagosRes.data && pagosRes.data.length > 0) {
                      const pago = pagosRes.data[0];
                      pagoInfo = {
                        metodoPago: pago.metodoPago || pago.metodo_pago || pago.MetodoPago,
                        referencia: pago.referencia || pago.Referencia,
                        montoPagado: pago.montoUsd || pago.MontoUsd,
                        fechaPago: pago.fechaPago || pago.FechaPago
                      };
                      break;
                    }
                  }
                }
              }
              if (pagoInfo) break;
            }
          }
        } catch (e) {
          console.warn("No se pudo obtener información de pago:", e);
        }
        
      } catch (e) { 
        console.warn("No se pudieron cargar detalles para el ticket", e); 
      }

      let clienteObj = ordenCompleta.cliente || {};
      
      if ((!clienteObj.telefono || !clienteObj.nombre) && (ordenCompleta.idCliente || item.idCliente)) {
        try {
          const resCli = await obtenerClientePorId(ordenCompleta.idCliente || item.idCliente);
          if (resCli.success && resCli.data) {
            clienteObj = resCli.data;
          }
        } catch (e) { console.warn("Error cargando cliente extra", e); }
      }

      const nombreFull = clienteObj.nombre 
        ? `${clienteObj.nombre} ${clienteObj.apellido || ''}`.trim()
        : (ordenCompleta.nombreCliente || item.nombreCliente);

      const cliente = {
        nombre: nombreFull,
        documento: clienteObj.documento || ordenCompleta.documentoCliente || item.documentoCliente,
        telefono: clienteObj.telefono || ordenCompleta.telefonoCliente || item.telefonoCliente || 'S/D'
      };

      const nombreEquipo = ordenCompleta.productoServicios?.nombre 
        || ordenCompleta.nombreProductoServicio 
        || item.nombreProductoServicio 
        || 'EQUIPO';

      let descLimpia = ordenCompleta.descripcion || item.descripcion || 'NO ESPECIFICADO';
      if (nombreEquipo && nombreEquipo !== 'EQUIPO' && descLimpia.toUpperCase().startsWith(nombreEquipo.toUpperCase())) {
        descLimpia = descLimpia.substring(nombreEquipo.length).trim();
      }

      const detalles = ordenCompleta.detalles || [];
      const servicios = [];
      const productos = [];
      
      detalles.forEach(det => {
        const precio = parseFloat(det.precioUnitarioUsd || det.subtotalUsd || 0);
        const garantia = det.garantiaDias || 0;
        
        if (det.idServicio) {
          servicios.push({
            nombre: det.nombreServicio || det.observaciones || 'Servicio',
            precio: precio,
            garantia: garantia,
            observaciones: det.observaciones
          });
        } else if (det.idProducto) {
          productos.push({
            nombre: det.nombreProducto || det.nombre || 'Producto',
            precio: precio,
            garantia: garantia
          });
        } else if (det.observaciones && det.observaciones.startsWith('REPUESTO:')) {
          servicios.push({
            nombre: det.observaciones.replace('REPUESTO:', '').trim(),
            precio: precio,
            garantia: 0,
            observaciones: det.observaciones
          });
        }
      });

      const ordenParaTicket = {
        ...ordenCompleta,
        ...item,
        equipo: nombreEquipo,
        descripcion: descLimpia,
        fechaIngreso: ordenCompleta.fechaIngreso || item.fechaIngreso,
        fechaEntrega: ordenCompleta.fechaEntrega || item.fechaEntrega,
        fechaPago: ordenCompleta.fechaPago || item.fechaPago || (pagoInfo?.fechaPago),
        montoPagadoUsd: ordenCompleta.montoPagadoUsd || item.montoPagadoUsd || pagoInfo?.montoPagado || (item.precioTotalUsd || 0),
        metodoPago: pagoInfo?.metodoPago || ordenCompleta.metodoPago || item.metodoPago || item.MetodoPago,
        referenciaPago: pagoInfo?.referencia || ordenCompleta.referenciaPago || item.referenciaPago || item.Referencia,
        servicios: servicios,
        productos: productos,
        garantiaDias: ordenCompleta.garantiaDias || item.garantiaDias || 0,
        tasaBsUsd: ordenCompleta.tasaBsUsd || item.tasaBsUsd || this.tasaActiva?.precio_usd_bs || 1,
        descuento: ordenCompleta.descuento || item.descuento || 0
      };

      const tecnico = {
        nombre: ordenParaTicket.nombreTecnicoRecepcion || item.nombreTecnicoRecepcion || 'TÉCNICO'
      };

      imprimirTicket(ordenParaTicket, cliente, tecnico, pagoInfo);
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.delay-100 { animation-delay: 0.1s; }
</style>